import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { SERVICES, SECTORS } from "@/data/site";
import Reveal from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import PageHeader, { Eyebrow, usePageTitle } from "@/components/PageHeader";

const ServiceRow = ({ s, reverse }) => (
  <div data-testid={`service-${s.id}`} className="grid grid-cols-1 items-center gap-10 border-t border-white/10 py-16 lg:grid-cols-2 lg:gap-20 md:py-24">
    <Reveal className={`overflow-hidden rounded-xl border border-white/10 ${reverse ? "lg:order-2" : ""}`}>
      <img src={s.image} alt={s.title} loading="lazy" className="aspect-[4/3] w-full object-cover opacity-85" />
    </Reveal>
    <Reveal delay={150}>
      <span className="font-display text-sm font-bold text-brand">{s.n}</span>
      <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] text-white md:text-6xl">{s.title}</h2>
      <p className="mt-6 max-w-xl text-base leading-relaxed text-white/60">{s.text}</p>
      <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {s.bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-sm text-white/75">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
            {b}
          </li>
        ))}
      </ul>
      {s.link && (
        <Link to={s.link} data-testid={`service-link-${s.id}`} className="mt-8 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand hover:text-brand-hover">
          Approfondisci il servizio <ArrowRight className="h-4 w-4" />
        </Link>
      )}
    </Reveal>
  </div>
);

export default function Servizi() {
  usePageTitle("/servizi");
  return (
    <main data-testid="servizi-page">
      <PageHeader
        testId="servizi-header"
        eyebrow="Cosa facciamo"
        title="Servizi"
        lead="Tre specializzazioni, un unico standard: progettazione e realizzazione a regola d'arte per il settore civile, industriale ed enti pubblici."
      />
      <section className="bg-ink">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          {SERVICES.map((s, i) => (
            <ServiceRow key={s.id} s={s} reverse={i % 2 === 1} />
          ))}
        </div>
      </section>
      <section data-testid="sectors-section" className="border-t border-white/10 bg-surface">
        <div className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-32">
          <Reveal>
            <Eyebrow>Settori serviti</Eyebrow>
            <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.95] text-white md:text-7xl">
              Dove lavoriamo<span className="text-brand">.</span>
            </h2>
            <p className="mt-6 max-w-xl text-base text-white/55">
              Interveniamo in tutta Italia, con competenze trasversali dal privato al grande appalto pubblico.
            </p>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {SECTORS.map((s, i) => (
              <Reveal key={s.n} delay={i * 100} className="bg-surface p-8" data-testid={`sector-${s.n}`}>
                <span className="font-display text-sm font-bold text-brand">{s.n}</span>
                <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white">{s.title}</h3>
                <p className="mt-3 text-sm text-white/55">{s.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </main>
  );
}
